import React, { useCallback, ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FormatService from '../../../services/format-service';
import {
  ROUTE_SETTINGS_ACCOUNT, ROUTE_SETTINGS_ACCOUNT_LIST, ROUTE_SETTINGS_DEAL_TEMPLATE_LIST, ROUTE_SETTINGS_NOTIFICATIONS,
  ROUTE_SETTINGS_OPERATION_LOG, ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST, ROUTE_SETTINGS_PROFILE, ROUTE_SETTINGS_SECURITY,
  ROUTE_SETTINGS_USER_LIST,
} from '../../../constants/system/routes';
import { QUERY_PARAM_ACTIVE, QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import {
  SETTINGS_ACCOUNT_VIEW, SETTINGS_USER_LIST_VIEW, SETTINGS_PROFILE_VIEW, SETTINGS_NOTIFICATION_VIEW,
  PORTFOLIO_GROUP_LIST_VIEW, DEAL_TEMPLATE_LIST_VIEW, ACCOUNT_LIST_VIEW, SETTINGS_OPERATION_LOG_VIEW,
} from '../../../constants/entity/permissions';
import {
  TAB_NAME_ACCOUNT, TAB_NAME_ACCOUNT_ADMINISTRATION, TAB_NAME_AUDIT_LOG, TAB_NAME_DEAL_TEMPLATES,
  TAB_NAME_PORTFOLIO_MANAGEMENT, TAB_NAME_USERS, TAB_NAME_PROFILE, TAB_NAME_SECURITY, TAB_NAME_NOTIFICATIONS,
} from '../../../constants/system/tab-names';

const SettingsPageTabs: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDisabled,
    isCompressed,
    activeTab,
    currentUser,
    onBeforeLocationChange,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_ACCOUNT,
      title: 'Account',
      permission: SETTINGS_ACCOUNT_VIEW,
      route: ROUTE_SETTINGS_ACCOUNT,
    },
    {
      id: 2,
      name: TAB_NAME_ACCOUNT_ADMINISTRATION,
      title: 'Account Administration',
      permission: ACCOUNT_LIST_VIEW,
      route: ROUTE_SETTINGS_ACCOUNT_LIST,
      isHidden: !currentUser || !currentUser.isPlatformAdmin,
    },
    {
      id: 3,
      name: TAB_NAME_AUDIT_LOG,
      title: 'Audit Log',
      permission: SETTINGS_OPERATION_LOG_VIEW,
      route: ROUTE_SETTINGS_OPERATION_LOG,
    },
    {
      id: 4,
      name: TAB_NAME_DEAL_TEMPLATES,
      title: 'Deal Templates',
      permission: DEAL_TEMPLATE_LIST_VIEW,
      route: ROUTE_SETTINGS_DEAL_TEMPLATE_LIST,
    },
    {
      id: 5,
      name: TAB_NAME_PORTFOLIO_MANAGEMENT,
      title: 'Portfolio Management',
      permission: PORTFOLIO_GROUP_LIST_VIEW,
      route: ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST,
    },
    {
      id: 6,
      name: TAB_NAME_USERS,
      title: 'Users',
      permission: SETTINGS_USER_LIST_VIEW,
      route: ROUTE_SETTINGS_USER_LIST,
      routeParams: {
        [QUERY_PARAM_ACTIVE]: FormatService.BOOLEAN_VALUE_TRUE,
      },
    },
    {
      id: 7,
      name: TAB_NAME_PROFILE,
      title: 'Profile',
      permission: SETTINGS_PROFILE_VIEW,
      route: ROUTE_SETTINGS_PROFILE,
    },
    {
      id: 8,
      name: TAB_NAME_SECURITY,
      title: 'Security',
      permission: SETTINGS_PROFILE_VIEW,
      route: ROUTE_SETTINGS_SECURITY,
    },
    {
      id: 9,
      name: TAB_NAME_NOTIFICATIONS,
      title: 'Notifications',
      permission: SETTINGS_NOTIFICATION_VIEW,
      route: ROUTE_SETTINGS_NOTIFICATIONS,
    },
  ];

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    const tabItem: TabItem = tabs[newTabIndex];
    const tabRoute: string | null = tabItem && tabItem.route ? tabItem.route : null;
    const tabQueryParams: LocationQueryParams = {
      ...(tabItem.routeParams || {}),
    };

    if (tabRoute && onBeforeLocationChange) {
      onBeforeLocationChange({
        pathname: tabRoute,
        search: FormatService.formatQueryParams(tabQueryParams),
      });
    }
  }, [tabs]);

  const onTabChange: Function = useCallback((newTabId: number): void => {
    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId && tabItem.route) {
        const tabQueryParams: LocationQueryParams = {
          ...(tabItem.routeParams || {}),
        };

        navigate({
          pathname: tabItem.route,
          search: FormatService.formatQueryParams(tabQueryParams),
        });
      }
    });
  }, [tabs]);

  const onTabClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tabId: number,
  ): void => {
    onTabChange(tabId);
  }, [tabs]);

  const permissions: string[] = tabs && tabs.length
    ? tabs.map((tabItem: TabItem): string | undefined => tabItem.permission)
      .filter((permissionItem: string | undefined): boolean => !!permissionItem) as string[] : [];

  return (
    <CheckAccess permissions={permissions}>
      <PageTabs
        isDisabled={isDisabled}
        isCompressed={isCompressed}
        tabs={tabs}
        currentTab={activeTab || queryTab}
        onTabChange={onTabChange}
        onBeforeTabChange={onBeforeTabChange}
        onClick={onTabClick}
      />
    </CheckAccess>
  );
});

export default SettingsPageTabs;
